import { createSelector } from "reselect";
import { getters } from "./model";
import * as FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";
import * as FormSelectors from "ui-kit/Form/selectors";
import * as R from "ramda";
import { TASK_TAB_IDS } from "./constants";
import moment from "moment";
import { getFieldValueToShow } from "Portal/NewPortal/Sections/Profile/selectors";

const SINGLE_ROW_FIELD_TYPES = ["textarea", "wysiwyg"];
const SINGLE_ROW_FIELD_IDS = [
  FIELD_IDS.ACCOUNTS.DESCRIPTION,
  FIELD_IDS.CONTACTS.ABOUT
];
const HIDE_FIELDS_FROM_EDIT = [
  FIELD_IDS.CONTACTS.AVATAR_URL,
  FIELD_IDS.ACCOUNTS.LOGO_URL
];

export const getEditedField = R.compose(
  field => R.prop("value", field),
  FormSelectors.getField
);

export const getTasks = createSelector(
  getters.tasks,
  getters.selectedTaskTab,
  (tasks, selectedTaskTab) => {
    let allTasks = [
      ...tasks.tasks.due_next_7_days,
      ...tasks.tasks.due_this_month,
      ...tasks.tasks.past_due
    ];

    if (selectedTaskTab === TASK_TAB_IDS.COMPLETE) {
      allTasks = R.filter(task => task.is_complete, allTasks);
    } else if (selectedTaskTab === TASK_TAB_IDS.INCOMPLETE) {
      allTasks = R.filter(task => !task.is_complete)(allTasks);
    } else if (selectedTaskTab === TASK_TAB_IDS.DUE) {
      allTasks = R.filter(
        task => task.due_raw && moment().isAfter(moment(task.due_raw))
      )(allTasks);
    }

    return R.sortBy(task => tasks.task_order[task.id])(allTasks);
  }
);

export const getEditableFields = createSelector(getters.fields, iniFields => {
  let fields = iniFields;
  fields = fields.filter(
    f => f.is_editable && !HIDE_FIELDS_FROM_EDIT.includes(f.id)
  );

  // pair event fields by every 2 fields
  const pairedFields = [];
  let i = 0;

  while (i < fields.length) {
    if (
      SINGLE_ROW_FIELD_TYPES.includes(fields[i]?.type) ||
      SINGLE_ROW_FIELD_IDS.includes(fields[i]?.id) ||
      SINGLE_ROW_FIELD_TYPES.includes(fields[i + 1]?.type) ||
      SINGLE_ROW_FIELD_IDS.includes(fields[i + 1]?.id)
    ) {
      pairedFields.push([fields[i]].filter(f => f));
      i += 1;
    } else {
      pairedFields.push([fields[i], fields[i + 1]].filter(f => f));
      i += 2;
    }
  }

  return pairedFields;
});

export const getFields = createSelector(getters.fields, fields => {
  return fields.filter(
    f =>
      ![
        FIELD_IDS.ACCOUNTS.LOGO_URL,
        FIELD_IDS.ACCOUNTS.LINKEDIN_URL,
        FIELD_IDS.ACCOUNTS.TWITTER_URL,
        FIELD_IDS.ACCOUNTS.WEBSITE,
        FIELD_IDS.ACCOUNTS.NAME,

        //
        FIELD_IDS.CONTACTS.AVATAR_URL,
        FIELD_IDS.CONTACTS.FIRST_NAME,
        FIELD_IDS.CONTACTS.LAST_NAME,
        FIELD_IDS.CONTACTS.EMAIL,
        FIELD_IDS.CONTACTS.TITLE,
        FIELD_IDS.CONTACTS.COMPANY_NAME,
        FIELD_IDS.CONTACTS.LINKEDIN_URL,
        FIELD_IDS.CONTACTS.TWITTER_URL,
        FIELD_IDS.CONTACTS.WEBSITE,
        FIELD_IDS.CONTACTS.HONORIFIC
      ].includes(f.id)
  );
});

export const getFormHasChanges = createSelector(
  getters.updatedFieldValues,
  updatedFieldValues => {
    return Object.keys(updatedFieldValues).length > 0;
  }
);

export const getIsValid = createSelector(getters.updatedFieldValues, () => {
  // @TODO: Add required check
  return true;
});

export const getProfilePhotoValue = createSelector(
  getters.values,
  getters.updatedFieldValues,
  getters.accountId,
  getters.isEditingDetails,
  getters.bio,
  (data, updatedData, accountId, isEditingDetails, bio) => {
    if (!isEditingDetails) {
      return bio.photo_url;
    }

    const value = getFieldValueToShow({
      fieldId: accountId
        ? FIELD_IDS.ACCOUNTS.LOGO_URL
        : FIELD_IDS.CONTACTS.AVATAR_URL,
      data,
      updatedData,
      defaultValue: null
    });

    const decoratedValue =
      R.prop("url")(value) && R.prop("url")(value).length
        ? R.prop("url")(value)
        : R.path([0, "url"])(value)
        ? R.path([0, "url"])(value)
        : value && value.length
        ? value
        : null;

    return decoratedValue;
  }
);
